import {
  FluidColor,
  FluidFieldValidation,
  FluidFormElementState,
  FluidPopoverConfig,
  FluidTextOverflow,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Prop,
  State,
} from '@stencil/core';

import { InfoPopover } from '../../common/fluid-common-icon.components';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';
import { hasError, isRequired } from '../../utils/validation/validation.utils';
import {
  FormLabelText,
  SupplementaryLabel,
} from './components/fluid-form-label.components';

/**
 * @displayName Form Label
 * @internal
 * @builder true
 *
 */
@Component({
  tag: 'fluid-form-label',
  styleUrl: 'fluid-form-label.less',
  shadow: true,
})
export class FluidFormLabel implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * True if browser is IE11, false if any other browser.
   */
  private _browserIsMsie: boolean = Utils.browserIsMsie();

  /**
   * Reference to the Form Label's inner <span>.
   */
  private _labelRef: HTMLElement;

  /**
   * Reference to ResizeObserver instance that monitors Form Label's
   * embedded <span> for width changes that cause overflow.
   */
  private _resizeObserver: ResizeObserver;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidFormLabelElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: { label?: string };
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * Tracks whether "helpText" Popover is open or closed.
   */
  @State() _popoverOpen = false;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   * @type {string}
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * A muted label to show after the main form label
   */
  @Prop({ mutable: true }) supplementaryLabel: string;

  /**
   * If provided, Form Label will render a "helptext" icon with attached
   * Popover adjacent to its label. To render a basic Popover with default
   * Prop values, simply provide the Popover's message as a string
   * value to this Prop. To render a Popover with customized Props,
   * provide a FluidPopoverConfig object to this Prop with the "message"
   * property set. For more information, please see Popover's API
   * documentation.
   */
  @Prop({ mutable: true }) helpText: string | FluidPopoverConfig;

  /**
   * A label to show with the input field (where applicable)
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Used by the form elements to tell the label if they've been touched or not.
   */
  @Prop({ mutable: true }) touched: boolean;

  /**
   * An array of field validation options
   */
  @Prop({ mutable: true }) validation: FluidFieldValidation[] = [];

  /**
   * The state of the element using the form label.
   */
  @Prop({ mutable: true }) elementState: FluidFormElementState = { errors: [] };

  /**
   * Whether the form element the label is attached to is disabled or not
   */
  @Prop({ mutable: true }) disabled: boolean;

  /**
   * Sets the text overflow behavior of the "label" Prop text.
   */
  @Prop({ mutable: true }) textOverflow: FluidTextOverflow =
    FluidTextOverflow.ELLIPSIS;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    useI18n.bind(this)();
    Utils.setGlobalTheme(this);
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action setOverflowTooltipText - sets "title" attr on label if text overflows width
   * @action initializeResizeObserver - begins watching for label size changes
   */
  componentDidLoad() {
    this.setOverflowTooltipText();
    this.initializeResizeObserver();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action _resizeObserver.disconnect - stop ResizeObserver from watching for changes
   */
  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;
    this._labelRef = undefined;
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Initializes ResizeObserver that watches for changes in the size of
   * the Form Label <span> element (due to changes in viewport size, theme,
   * Prop values, etc.). More reliable/accurate & less resource-intensive
   * than "window.resize" listener.
   */
  initializeResizeObserver(): void {
    if (
      this._browserIsMsie ||
      typeof ResizeObserver === 'undefined' ||
      isNullOrUndefined(this._labelRef)
    )
      return;

    this._resizeObserver = new ResizeObserver(
      Utils.debounce((entries) => {
        if (entries.length !== 1) return;
        const labelWidth = Math.ceil(entries[0]?.borderBoxSize[0]?.inlineSize);
        this.setOverflowTooltipText(labelWidth);
      })
    );
    this._resizeObserver.observe(this._labelRef);
  }

  /**
   * Updates internal "_popoverOpen" State when Popover is opened/closed.
   * Ultimately controls "z-index" of Popover - sets a higher value if
   * Popover is open so that it appears on top of any other sibling
   * Form Labels.
   * @param event - "popoverToggled" event emitted by Popover
   */
  onPopoverToggledHandler(event): void {
    this._popoverOpen = event.detail;
  }

  /**
   * If Form Label's text overflows its container & is truncated, sets
   * "title" attribute that will display full "label" text as tooltip
   * on :hover.
   * @param labelWidth - label width from ResizeObserver OR clientWidth()
   */
  setOverflowTooltipText(
    labelWidth: number = this._labelRef?.clientWidth
  ): void {
    if (!this.label || !this._labelRef) return;

    if (this._labelRef.scrollWidth > labelWidth) {
      this._labelRef.title = this.label;
    } else {
      this._labelRef.removeAttribute('title');
    }
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    if (!this.label && !this.fluidTranslate && !this.fluidTranslate?.label)
      return;

    return (
      <div
        class={{
          [`fluid-form-label ${this.theme}`]: true,
          inline: this.inlineQuestion,
        }}>
        <div
          class={{
            'label-container': true,
            'reset-position':
              this.resetPosition ||
              this.textOverflow === FluidTextOverflow.WRAP,
            'popover-open': this._popoverOpen,
            'form-element-disabled': this.disabled,
          }}>
          <label
            htmlFor={this.elementState.name}
            class={{
              'form-label': true,
              [`text-overflow-${this.textOverflow}`]: true,
              'inline-question': this.inlineQuestion,
              'has-error': hasError(this.elementState, this.touched),
              'form-element-disabled': this.disabled,
            }}>
            <FormLabelText
              label={this.label}
              i18nStore={this._i18nStore}
              fluidTranslate={this.fluidTranslate}
              setRef={(el) => (this._labelRef = el as HTMLElement)}
            />
            <SupplementaryLabel
              supplementaryLabel={this.supplementaryLabel}
              i18nStore={this._i18nStore}
              fluidTranslate={this.fluidTranslate}
            />
            {!!isRequired(this.validation) && <span class='required'>*</span>}
            {!!this.helpText && (
              <InfoPopover
                helpText={this.helpText}
                iconColor={FluidColor.BLUE}
                onPopoverToggled={(e) => this.onPopoverToggledHandler(e)}
                theme={this.theme}
                overrideGlobalTheme={this.overrideGlobalTheme}
              />
            )}
          </label>
        </div>
      </div>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Overrides default theme-specific positioning of Form Label container.
   * @internal
   */
  @Prop({ mutable: true }) resetPosition = false;

  /**
   * If true, will style the label as an inline question (so matching option labels)
   * @internal
   */
  @Prop({ mutable: true }) inlineQuestion = false;
}
